import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import {
  getTableConfig,
  ONBOARDING_STATUS,
  TABLE_TYPES,
} from "../config/tableConfig";
import OnboardingFormDialog from "../forms/OnboardingFormDialog";
import CardAdditionFormDialog from "../forms/CardAdditionFormDialog";
import { Close } from "@mui/icons-material";
import CrifCibilEntryStep from "../../driverLeadCreationV2/steps/CrifCibilEntryStep";

const DetailsDialog = ({
  open,
  data,
  onClose,
  tableType,
  DetailsComponent,
}) => {
  const [dialogTitle, setDialogTitle] = useState("");

  useEffect(() => {
    switch (tableType) {
      case TABLE_TYPES.PRE_ONBOARDING:
        setDialogTitle("Pre-Onboarding");
        break;
      case TABLE_TYPES.ONBOARDED:
        setDialogTitle("Onboarding");
        break;
      case TABLE_TYPES.LEADS:
        setDialogTitle("Lead");
        break;

      default:
        break;
    }
  }, [tableType]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {`${dialogTitle} Details`}
      </DialogTitle>
      <DialogContent>
        {DetailsComponent ? (
          <DetailsComponent data={data} />
        ) : (
          <Typography color="textSecondary">No component provided</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const StatusUpdateDialog = ({
  open,
  data,
  onClose,
  onConfirm,
  tableType,
  isLoading,
}) => {
  const [newStatus, setNewStatus] = useState(data?.status || "");
  const config = getTableConfig(tableType);

  const isLeadTable = tableType === TABLE_TYPES.LEADS;

  useEffect(() => {
    if (open && data) {
      if (isLeadTable) setNewStatus(data.onboardingStatus || "");
      else {
        setNewStatus(data.status || "");
      }
    }
  }, [open, data]);

  const handleConfirm = () => {
    if (newStatus !== data?.status || newStatus !== data?.onboardingStatus) {
      onConfirm(newStatus);
    }
  };

  const getDialogTitle = () => {
    if (
      tableType === TABLE_TYPES.PRE_ONBOARDING ||
      tableType === TABLE_TYPES.ONBOARDED
    ) {
      return `Update Status - ${data?.prn || ""}`;
    }
    if (tableType === TABLE_TYPES.LEADS) {
      return `Update Status - ${data?.leadId || ""}`;
    }
    return `Update Status - ${data?.batteryId || ""}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>{getDialogTitle()}</DialogTitle>
      <DialogContent sx={{ py: 3, mt: 3 }}>
        {data && (
          <>
            <Box
              sx={{ my: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
            >
              <Typography variant="caption" color="textSecondary">
                {isLeadTable ? "Current Onboarding Status" : "Current Status"}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {isLeadTable ? data.onboardingStatus : data.status || "--"}
              </Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel>
                {isLeadTable ? "New Onboarding Status" : "New Status"}
              </InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => {
                  setNewStatus(e.target.value);
                }}
                label={isLeadTable ? "New Onboarding Status" : "New Status"}
                disabled={isLoading}
              >
                {config.statusOptions.map((option) => {
                  if (
                    option.value.toLocaleLowerCase() ===
                      ONBOARDING_STATUS.DOC_CHANGE_REQUESTED.toLocaleLowerCase() &&
                    !data?.leadId
                  ) {
                    return null;
                  }
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || newStatus === data?.status}
          endIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LeadFeedbackDialog = ({
  open,
  data,
  onClose,
  onConfirm,
  tableType,
  isLoading,
  refetchTableData,
  leadFeedbackComponent: LeadFeedback,
}) => {
  const [newStatus, setNewStatus] = React.useState(data?.status || "");
  const config = getTableConfig(tableType);

  React.useEffect(() => {
    if (open && data) {
      setNewStatus(data.status || "");
    }
  }, [open, data]);

  const handleConfirm = () => {
    if (newStatus !== data?.status) {
      onConfirm(newStatus);
    }
  };

  const getDialogTitle = () => {
    if (tableType === TABLE_TYPES.PRE_ONBOARDING) {
      return `Update Status - ${data?.prn || ""}`;
    }
    if (tableType === TABLE_TYPES.LEADS) {
      return `Update Status - ${data?.leadId || ""}`;
    }
    return `Update Status - ${data?.batteryId || ""}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {data?.leadId}

        <IconButton onClick={onClose} size="small" aria-label="close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {LeadFeedback ? (
          <LeadFeedback
            key={data?.leadId}
            leadId={data?.leadId}
            feedbackStatus={data?.feedbackStatus}
            refetchTableData={refetchTableData}
          />
        ) : (
          <Typography color="textSecondary">No component provided</Typography>
        )}
      </DialogContent>
      {/* <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || newStatus === data?.status}
          endIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? "Updating..." : "Update"}
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

const CrifReportDialog = ({
  open,
  data,
  onClose,
  onConfirm,
  tableType,
  isLoading,
  refetchTableData,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {data?.leadId}

        <IconButton onClick={onClose} size="small" aria-label="close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <CrifCibilEntryStep data={data} refetchTableData={refetchTableData} />
      </DialogContent>
      {/* <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || newStatus === data?.status}
          endIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? "Updating..." : "Update"}
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

const DeleteDialog = ({
  open,
  data,
  onClose,
  onConfirm,
  isLoading,
  error,
  tableType,
}) => {
  const getDisplayName = () => {
    switch (tableType) {
      case TABLE_TYPES.PRE_ONBOARDING:
        return data?.prn || "this record";
      case TABLE_TYPES.LEADS:
        return data?.leadId || "this lead";
      default:
        return data?.batteryId || "this record";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, color: "error.main" }}>
        Confirm Delete
      </DialogTitle>
      <DialogContent sx={{ py: 3, mt: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Are you sure you want to delete {getDisplayName()}? This action cannot
          be undone.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error.message || "Failed to delete. Please try again."}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(data?.id)}
          variant="contained"
          color="error"
          disabled={isLoading}
          endIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FinancierChangeRequestDialog = ({
  open,
  data,
  onClose,
  onConfirm,
  isLoading,
  error,
  tableType,
}) => {
  const getDisplayName = () => {
    switch (tableType) {
      case TABLE_TYPES.PRE_ONBOARDING:
        return data?.prn || "this record";
      case TABLE_TYPES.LEADS:
        return data?.leadId || "this lead";
      default:
        return data?.batteryId || "this record";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, color: "error.main" }}>
        Send Financier Change Request
      </DialogTitle>
      <DialogContent sx={{ py: 3, mt: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Are you sure you want to send a financier change request for{" "}
          {data?.prn}? This action cannot be undone.
        </Alert>

        <Alert severity="success" sx={{ mb: 2 }}>
          Current Financier: {data?.loanCalculationSummary?.financier || "--"}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error.message || "Failed to delete. Please try again."}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(data?.id)}
          variant="contained"
          color="error"
          disabled={isLoading}
          endIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? "Requesting..." : "Change Financier"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogsSection = ({
  dialogs,
  tableType,
  DetailsComponent,
  ActionBarComponent,
  onStatusUpdate,
  onUpdateData,
  onDelete,
  statusUpdateLoading,
  deleteLoading,
  deleteError,
  refetchTableData,
  onPreOnboardDoc,
  leadFeedbackComponent,
  onAllUpdate,
  onFinancierChangeRequest,
  financierChangeRequestLoading,
  financierChangeRequestError,
}) => {
  return (
    <>
      {ActionBarComponent && (
        <ActionBarComponent
          open={dialogs.menu.open}
          anchorEl={dialogs.menu.anchorEl}
          onClose={dialogs.menu.onClose}
          onView={dialogs.rowActions.handleView}
          onEdit={dialogs.rowActions.handleEdit}
          onDelete={dialogs.rowActions.handleDelete}
          onOnboard={dialogs.rowActions.handleOnboard}
          onUpdateData={onUpdateData}
          onPreOnboardDoc={onPreOnboardDoc}
          rowData={dialogs?.menu?.data}
          tableType={tableType}
          onLeadFeedback={dialogs.rowActions.handleLeadFeedback}
          onCrifReport={dialogs.rowActions.handleCrifReport}
          onAllUpdate={onAllUpdate}
          onFinancierChangeRequest={
            dialogs.rowActions.handleFinancierChangeRequest
          }
        />
      )}

      <DetailsDialog
        open={dialogs.detailsDialog.open}
        data={dialogs.detailsDialog.data}
        onClose={dialogs.detailsDialog.onClose}
        tableType={tableType}
        DetailsComponent={DetailsComponent}
      />

      <LeadFeedbackDialog
        open={dialogs.leadFeedbackDialog.open}
        data={dialogs.leadFeedbackDialog.data}
        onClose={dialogs.leadFeedbackDialog.onClose}
        tableType={tableType}
        leadFeedbackComponent={leadFeedbackComponent}
        refetchTableData={refetchTableData}
      />

      <CrifReportDialog
        open={dialogs.crifReportOpen.open}
        data={dialogs.crifReportOpen.data}
        onClose={dialogs.crifReportOpen.onClose}
        tableType={tableType}
        refetchTableData={refetchTableData}
      />

      <StatusUpdateDialog
        open={dialogs.statusDialog.open}
        data={dialogs.statusDialog.data}
        onClose={dialogs.statusDialog.onClose}
        onConfirm={onStatusUpdate}
        tableType={tableType}
        isLoading={statusUpdateLoading}
      />

      <DeleteDialog
        open={dialogs.deleteDialog.open}
        data={dialogs.deleteDialog.data}
        onClose={dialogs.deleteDialog.onClose}
        onConfirm={onDelete}
        tableType={tableType}
        isLoading={deleteLoading}
        error={deleteError}
      />

      <FinancierChangeRequestDialog
        open={dialogs.financierChangeRequestDialog.open}
        data={dialogs.financierChangeRequestDialog.data}
        onClose={dialogs.financierChangeRequestDialog.onClose}
        onConfirm={onFinancierChangeRequest}
        tableType={tableType}
        isLoading={financierChangeRequestLoading}
        error={financierChangeRequestError}
      />

      <OnboardingFormDialog
        open={dialogs.onboardingDialog.open}
        data={dialogs.onboardingDialog.data}
        onClose={dialogs.onboardingDialog.onClose}
      />

      <CardAdditionFormDialog
        open={dialogs.cardAddition.open}
        data={dialogs.cardAddition.data}
        onClose={dialogs.cardAddition.onClose}
        refetchTableData={refetchTableData}
      />
    </>
  );
};

export default DialogsSection;
export {
  DetailsDialog,
  StatusUpdateDialog,
  DeleteDialog,
  FinancierChangeRequestDialog,
};
